import React from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

import { SiteLayout } from "@/components/layout/SiteLayout";
import { LegalLayout } from "@/components/layout/LegalLayout";
import { ToastProvider } from "@/components/ui/Toast";
import { LenisProvider } from "@/components/motion/LenisProvider";
import { PageTransition } from "@/components/motion/PageTransition";
import { LocaleProvider } from "@/components/LocaleProvider";
import { FirstVisitRedirect } from "@/components/FirstVisitRedirect";
import { LanguageTransitionProvider } from "@/components/LanguageTransitionProvider";
import { AwardsLoader } from "@/components/AwardsLoader";
import { FeatureFlagsProvider } from "@/hooks/useFeatureFlags";
import { VerifiedDocsGate } from "@/components/VerifiedDocsGate";

import Home from "@/pages/Home";
import Platform from "@/pages/Platform";
import PlatformMarketing from "@/pages/PlatformMarketing";
import PlatformAI from "@/pages/PlatformAI";
import PlatformData from "@/pages/PlatformData";
import PlatformEnergy from "@/pages/PlatformEnergy";
import PlatformTokenization from "@/pages/PlatformTokenization";
import Intelligence from "@/pages/Intelligence";
import Solutions from "@/pages/Solutions";
import Company from "@/pages/Company";
import Newsroom from "@/pages/Newsroom";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NotFound from "@/pages/NotFound";
import KitchenSink from "@/pages/KitchenSink";
import SceneLab from "@/pages/SceneLab";
import VerifiedDocuments from "@/pages/VerifiedDocuments";

// Every dark-surface page is served both at `/<path>` and `/zh/<path>`.
// Locale is derived from the URL — one component tree, i18n drives strings.
const renderDarkRoutes = () => (
  <>
    <Route index element={<Home />} />
    <Route path="platform" element={<Platform />} />
    <Route path="platform/marketing" element={<PlatformMarketing />} />
    <Route path="platform/ai" element={<PlatformAI />} />
    <Route path="platform/data" element={<PlatformData />} />
    <Route path="platform/energy" element={<PlatformEnergy />} />
    <Route path="platform/tokenization" element={<PlatformTokenization />} />
    <Route path="intelligence" element={<Intelligence />} />
    <Route path="solutions" element={<Solutions />} />
    <Route path="company" element={<Company />} />
    <Route path="newsroom" element={<Newsroom />} />
    <Route path="contact" element={<Contact />} />
    <Route path="verified-documents" element={
      <VerifiedDocsGate>
        <VerifiedDocuments />
      </VerifiedDocsGate>
    } />
    <Route path="*" element={<NotFound />} />
  </>
);

function App() {
  return (
    <HelmetProvider>
      <ToastProvider>
        <BrowserRouter>
          <LocaleProvider>
            <LanguageTransitionProvider>
              <AwardsLoader />
              <FeatureFlagsProvider>
                <LenisProvider>
                  <PageTransition>
                    <FirstVisitRedirect>
                      <Routes>
                        {/* 301-style redirects (client-side) */}
                        <Route
                          path="/investor-relations"
                          element={<Navigate to="/" replace />}
                        />
                        <Route
                          path="/the-frac-token"
                          element={<Navigate to="/" replace />}
                        />
                        <Route
                          path="/products"
                          element={<Navigate to="/platform" replace />}
                        />

                        {/* Legal pages are English-only. Their /zh mirrors
                            301-redirect to the English canonical. */}
                        <Route path="/zh/privacy" element={<Navigate to="/privacy" replace />} />
                        <Route path="/zh/terms" element={<Navigate to="/terms" replace />} />

                        {/* Internal debug surface */}
                        <Route path="/_kitchen-sink" element={<KitchenSink />} />
                        <Route path="/_scene-lab" element={<SceneLab />} />

                        {/* English dark routes */}
                        <Route element={<SiteLayout />}>
                          {renderDarkRoutes()}
                        </Route>

                        {/* Mandarin dark routes — same component tree, i18n
                            drives the strings, /zh URL prefix identifies locale. */}
                        <Route path="/zh" element={<SiteLayout />}>
                          {renderDarkRoutes()}
                        </Route>

                        {/* Light-surface legal routes */}
                        <Route element={<LegalLayout />}>
                          <Route path="/privacy" element={<Privacy />} />
                          <Route path="/terms" element={<Terms />} />
                        </Route>
                      </Routes>
                    </FirstVisitRedirect>
                  </PageTransition>
                </LenisProvider>
              </FeatureFlagsProvider>
            </LanguageTransitionProvider>
          </LocaleProvider>
        </BrowserRouter>
      </ToastProvider>
    </HelmetProvider>
  );
}

export default App;
